import { useCallback, useEffect, useRef, useState } from 'react';

import {
  CircularProgress,
  Typography,
  alpha,
  makeStyles,
} from '@material-ui/core';

import {
  RiCheckboxCircleFill,
  RiCloseCircleFill,
  RiTimeFill,
} from '@remixicon/react';
import classNames from 'classnames';

const PHRASES = [
  // Setting up
  'Preparing your workspace',

  // Flags & targeting
  'Booting up feature flagging',
  'Warming the flag resolver',
  'Materializing segments',

  // Metrics
  'Staging the warehouse connection',
  'Organizing the metrics catalog',
  'Optimizing aggregations',
  'Arming guardrail metrics',

  // Stats (the nerdy ones)
  'Initializing the statistics engine',
  'Calibrating sequential analysis',
  'Tuning asymptotics',
  'Preparing sufficient statistics',

  // Experiments & surfaces
  'Orchestrating workflows',
  'Drafting experimentation strategies',
  'Mapping experimentation surfaces',
  'Partitioning audience segments',

  // Easter egg
  'Reticulating splines',
];

const FADE_DURATION_MS = 400;
const TYPE_SPEED_MS = 30;
const HOLD_MS = 400;
const DOT_SPEED_MS = 400;
const RESULT_DISPLAY_MS = 1000;

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(3),
  },
  textArea: {
    height: 24,
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
  },
  text: {
    whiteSpace: 'nowrap',
    position: 'absolute',
    transition: `opacity ${FADE_DURATION_MS}ms ease-in-out, transform ${FADE_DURATION_MS}ms ease-in-out`,
    color: alpha(theme.palette.text.primary, 0.7),
  },
  visible: {
    opacity: 1,
    transform: 'translateY(0)',
  },
  exiting: {
    opacity: 0,
    transform: 'translateY(12px)',
  },
  entering: {
    opacity: 1,
    transform: 'translateY(0)',
    transition: 'none',
  },
  dots: {
    position: 'absolute',
  },
  resultIcon: {
    transition: `opacity ${FADE_DURATION_MS}ms ease-in-out`,
  },
  successIcon: {
    color: theme.palette.success.main,
  },
  errorIcon: {
    color: theme.palette.error.main,
  },
  reviewIcon: {
    color: theme.palette.warning.main,
  },
  resultText: {
    color: alpha(theme.palette.text.primary, 0.7),
  },
}));

const useTypewriter = (speed: number) => {
  const [text, setText] = useState(PHRASES[0]);
  const [dots, setDots] = useState('...');
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clear = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  }, []);

  const startTyping = useCallback(
    (phrase: string, onComplete: () => void) => {
      clear();
      let i = 0;
      setText('');
      setDots('');

      const typeNext = () => {
        i++;
        setText(phrase.slice(0, i));
        if (i < phrase.length) {
          timeoutsRef.current.push(setTimeout(typeNext, speed));
        } else {
          let d = 0;
          const typeDot = () => {
            d++;
            setDots('.'.repeat(d));
            if (d < 3) {
              timeoutsRef.current.push(setTimeout(typeDot, DOT_SPEED_MS));
            } else {
              timeoutsRef.current.push(setTimeout(onComplete, HOLD_MS));
            }
          };
          timeoutsRef.current.push(setTimeout(typeDot, DOT_SPEED_MS));
        }
      };

      timeoutsRef.current.push(setTimeout(typeNext, speed));
    },
    [speed, clear],
  );

  useEffect(() => () => clear(), [clear]);

  return { text, dots, startTyping, clear };
};

type ExperimentationLoaderProps = {
  completed?: boolean;
  error?: Error;
  onResultDismissed?: () => void;
};

const parseErrorMessage = (error: Error) => {
  const match = error.message.match(/^\w+:\s*(.+)/);
  return match ? match[1] : error.message;
};

export const ExperimentationLoader = ({
  completed = false,
  error,
  onResultDismissed,
}: ExperimentationLoaderProps) => {
  const classes = useStyles();
  const indexRef = useRef(0);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const rafRef = useRef<number>();
  const [phase, setPhase] = useState<'visible' | 'exiting' | 'entering'>(
    'visible',
  );
  const [result, setResult] = useState<'success' | 'error' | 'review' | null>(
    null,
  );
  const [resultMessage, setResultMessage] = useState('');
  const { text, dots, startTyping, clear } = useTypewriter(TYPE_SPEED_MS);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = undefined;
    }
  }, []);

  const advance = useCallback(() => {
    setPhase('exiting');
    timersRef.current.push(
      setTimeout(() => {
        indexRef.current = (indexRef.current + 1) % PHRASES.length;
        setPhase('entering');
        startTyping(PHRASES[indexRef.current], advance);
        rafRef.current = requestAnimationFrame(() => {
          setPhase('visible');
        });
      }, FADE_DURATION_MS),
    );
  }, [startTyping]);

  // Kick off the first cycle after the initial hold
  useEffect(() => {
    const timeout = setTimeout(advance, HOLD_MS);
    timersRef.current.push(timeout);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clean up all timers on unmount
  useEffect(
    () => () => {
      clearTimers();
      clear();
    },
    [clearTimers, clear],
  );

  // Transition to completed or error state
  useEffect(() => {
    if (completed || error) {
      clearTimers();
      clear();
      setPhase('exiting');
      timersRef.current.push(
        setTimeout(() => {
          if (error) {
            const isReview = /review/i.test(error.message);
            setResult(isReview ? 'review' : 'error');
            setResultMessage(
              isReview
                ? 'Your account request is under review, you will get an e-mail once it is approved'
                : parseErrorMessage(error),
            );
          } else {
            setResult('success');
            setResultMessage('Account created');
          }
          timersRef.current.push(
            setTimeout(() => {
              onResultDismissed?.();
            }, RESULT_DISPLAY_MS),
          );
        }, FADE_DURATION_MS),
      );
    }
  }, [completed, error, clear, clearTimers, onResultDismissed]);

  if (result) {
    const icon = {
      success: (
        <RiCheckboxCircleFill
          className={classNames(classes.resultIcon, classes.successIcon)}
          size={40}
        />
      ),
      review: (
        <RiTimeFill
          className={classNames(classes.resultIcon, classes.reviewIcon)}
          size={40}
        />
      ),
      error: (
        <RiCloseCircleFill
          className={classNames(classes.resultIcon, classes.errorIcon)}
          size={40}
        />
      ),
    }[result];

    return (
      <div className={classes.container}>
        {icon}
        <Typography variant="body1" className={classes.resultText}>
          {resultMessage}
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <CircularProgress size={40} />
      <div className={classes.textArea}>
        <Typography
          variant="body1"
          className={classNames(classes.text, classes[phase])}
        >
          {text}
          <span className={classes.dots}>{dots}</span>
        </Typography>
      </div>
    </div>
  );
};
